import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Text,
  Container,
  Paper,
  Group,
  TextInput,
  Stack,
  Title,
  Divider,
  ScrollArea,
  Notification,
  Badge,
  Select,
} from "@mantine/core";
import {
  connectADB,
  installApk,
  uninstallApp,
  grantPermissions,
  PACKAGE_NAME,
  installedApkPath,
  rebootDevice,
  detectHardwareModel,
  disableDpvrKioskMode,
  releaseDeviceOwner,
  isAdbConnected,
  onAdbSessionLost,
  getSerial,
} from "@utils/webusb-utils/ADBUtils";
import { pushDeviceConfigsAndReboot } from "@utils/webusb-utils/pushDeviceConfigs";
import { showPermissionDialogsNotice } from "@utils/permissionDialogsNotice";
import { downloadReleaseApk } from "@utils/webusb-utils/releaseApkDownload";
import { getDefaultVersion } from "@utils/api-communication/HeadsetReleasesCli";
import {
  grantsPermissionsOnInstall,
  runsAsDeviceOwner,
} from "@utils/webusb-utils/headsetSetupSteps";
import { HardwareModels } from "types/productTypes";
import { hardwareNameOf } from "types/headsetReleaseTypes";
import { IconDownload, IconWifi } from "@tabler/icons-react";
import { openConfirmation } from "@utils/redux-utils/actions/actions";
import { openVABatchDownloadView, closeVABatchDownloadView } from "@store/ui/uiSlice";
import { selectIsVABatchDownloadViewOpen } from "@store/ui/uiSelectors";
import VABatchDownloadView from "./VABatchDownloadView";
import DownloadProgressBar, { DownloadProgressState } from "./DownloadProgressBar";
import { openDeviceSetupPage } from "@utils/deviceSetupNavigation";
import {
  defaultConfigKeyForIntegration,
  getConfigKeyOverrideOptions,
  normalizeConfigKeyToken,
} from "@utils/deviceConfigKeys";
import { getUserIntegrationKey } from "@utils/auth";
import { getRole, getUsername } from "@utils/CommonUtil";
import { Role } from "@model/role";

const InstallerWithWebUsb: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isVABatchDownloadViewOpen = useSelector(selectIsVABatchDownloadViewOpen);
  const [connected, setConnected] = useState(false);
  const [modelInfo, setModelInfo] = useState<string>("-");
  const [serialInfo, setSerialInfo] = useState<string>("");
  const [logs, setLogs] = useState<string[]>([]);
  const [versionInput, setVersionInput] = useState("");
  const [downloadMode, setDownloadMode] = useState(false);
  const [downloadedApkPath, setDownloadedApkPath] = useState<string | null>(null);
  const [apiConfigKey, setApiConfigKey] = useState<string | null>(null);
  const [defaultApiConfigKey, setDefaultApiConfigKey] = useState<string | null>(null);
  const apiConfigKeyTouched = useRef(false);
  const expectDisconnectRef = useRef(false);
  const role = getRole();
  const canOverrideApiConfig = role === Role.Admin || role === Role.SuperAdmin;
  const apiConfigKeyToPush = canOverrideApiConfig ? apiConfigKey : null;
  const [working, setWorking] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgressState | null>(null);
  const [notification, setNotification] = useState<null | {
    type: "success" | "error";
    msg: string;
  }>(null);
  const apiConfigOptions = useMemo(() => {
    const options: { value: string; label: string }[] = getConfigKeyOverrideOptions(t);
    if (defaultApiConfigKey && !options.some(({ value }) => value === defaultApiConfigKey)) {
      options.push({ value: defaultApiConfigKey, label: defaultApiConfigKey });
    }
    return options;
  }, [defaultApiConfigKey, t]);

  useEffect(() => {
    let active = true;
    void getUserIntegrationKey(getUsername()).then((integrationKey) => {
      if (!active) return;
      const defaultKey = defaultConfigKeyForIntegration(integrationKey);
      setDefaultApiConfigKey(defaultKey);
      if (!apiConfigKeyTouched.current) setApiConfigKey(defaultKey);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isAdbConnected()) return;
    void (async () => {
      try {
        const model = await detectHardwareModel();
        const serial = getSerial() ?? "";
        if (!isAdbConnected()) return;
        setModelInfo(model);
        setSerialInfo(serial);
        setConnected(true);
        setLogs((l) => [...l, "Using the existing USB connection"]);
      } catch (e: any) {
        setLogs((l) => [...l, "Could not read the connected device: " + (e?.message || e)]);
      }
    })();
  }, []);

  useEffect(
    () =>
      onAdbSessionLost(() => {
        if (expectDisconnectRef.current) return;
        setConnected(false);
        setModelInfo("-");
        setSerialInfo("");
        setLogs((l) => [...l, "USB connection lost; reconnect the headset once it is back up."]);
      }),
    [],
  );

  const appendLog = (line: string) => {
    setLogs((l) => [...l, line]);
  };

  const logCb = (msg: string) => {
    appendLog(msg);
    // try to parse model info from connect log
    if (msg.startsWith("Model:")) {
      setModelInfo(msg.replace("Model:", "").trim());
    }
    // try to parse serial info from connect log
    if (msg.startsWith("Serial:")) {
      setSerialInfo(msg.replace("Serial:", "").trim());
    }
  };

  const handleConnect = async () => {
    setWorking(true);
    expectDisconnectRef.current = false;
    try {
      await connectADB(logCb);
      setConnected(true);
      setNotification({ type: "success", msg: t("Installer.connected") });
    } catch (e: any) {
      appendLog("Connect error: " + (e?.message || e));
      setNotification({ type: "error", msg: t("Installer.connectFailed") });
      setConnected(false);
    } finally {
      setWorking(false);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleInstall = async () => {
    setWorking(true);
    try {
      appendLog(`Install requested (version: ${versionInput || "(none)"})`);
      // Use the downloaded apk path if available, otherwise fallback to installedApkPath
      const apkPathToInstall = downloadedApkPath || installedApkPath;
      appendLog(`Installing from: ${apkPathToInstall}`);
      await installApk(apkPathToInstall, (m) => appendLog(m));
      const model = await detectHardwareModel();
      if (grantsPermissionsOnInstall(model)) {
        appendLog("Granting permissions ...");
        await grantPermissions((m) => appendLog(m));
      }
      setNotification({ type: "success", msg: t("Installer.installFinished") });
    } catch (e: any) {
      appendLog("Install error: " + (e?.message || e));
      setNotification({ type: "error", msg: t("Installer.installFailed") });
    } finally {
      setWorking(false);
      // Remove the version field after Install clicked
      setVersionInput("");
      // Optionally keep downloadedApkPath so installation source persists; do not clear it here
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handleUninstall = async () => {
    setWorking(true);
    try {
      appendLog("Uninstall requested");
      const model = await detectHardwareModel();
      if (model === HardwareModels.Dpvr) {
        appendLog("Disabling kiosk mode ...");
        await disableDpvrKioskMode((m) => appendLog(m));
      }
      if (runsAsDeviceOwner(model)) {
        appendLog("Releasing the device owner role ...");
        await releaseDeviceOwner((m) => appendLog(m));
      }
      await uninstallApp(PACKAGE_NAME, (m) => appendLog(m));
      setNotification({ type: "success", msg: t("Installer.uninstallFinished") });
    } catch (e: any) {
      appendLog("Uninstall error: " + (e?.message || e));
      setNotification({ type: "error", msg: t("Installer.uninstallFailed") });
    } finally {
      setWorking(false);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const handlePushConfigs = async () => {
    setWorking(true);
    try {
      const { apiConfigPushFailed } = await pushDeviceConfigsAndReboot(
        apiConfigKeyToPush,
        (m) => appendLog(m),
        () => {
          expectDisconnectRef.current = true;
        },
      );
      if (apiConfigPushFailed) {
        setNotification({ type: "error", msg: t("Installer.configsPushedApiKeyFailed") });
      } else {
        setNotification({
          type: "success",
          msg:
            apiConfigKeyToPush === null
              ? t("Installer.configsPushed")
              : t("Installer.configsPushedApiKeyPending"),
        });
      }
      showPermissionDialogsNotice(dispatch, t);
    } catch (e: any) {
      appendLog("Push config error: " + (e?.message || e));
      setNotification({ type: "error", msg: t("Installer.pushConfigFailed") });
    } finally {
      setConnected(false);
      setWorking(false);
      apiConfigKeyTouched.current = false;
      setApiConfigKey(defaultApiConfigKey);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const reportDownloadProgress = (loaded: number, total?: number) => {
    setDownloadProgress({ loaded, total });
  };

  const runDownload = async (version: string) => {
    setWorking(true);
    setDownloadProgress({ loaded: 0 });
    try {
      const model = await detectHardwareModel();
      const res = await downloadReleaseApk(version, model, appendLog, reportDownloadProgress);
      appendLog(`Download completed: ${res.remotePath} (${res.size} bytes)`);
      setDownloadedApkPath(res.remotePath);
      setNotification({ type: "success", msg: t("Installer.downloadFinished") });
    } catch (e: any) {
      appendLog("Download error: " + (e?.message || e));
      setNotification({ type: "error", msg: t("Installer.downloadFailed") });
    } finally {
      setWorking(false);
      setDownloadMode(false);
      setDownloadProgress(null);
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const showDownloadError = (msg: string) => {
    setWorking(false);
    setNotification({ type: "error", msg });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleDownloadDefault = async () => {
    setWorking(true);
    const model = await detectHardwareModel().catch(() => null);
    if (!model) {
      showDownloadError(t("Installer.connectFailed"));
      return;
    }
    let defaultVersion;
    try {
      defaultVersion = await getDefaultVersion(model);
    } catch (e: any) {
      appendLog("Default version lookup error: " + (e?.message || e));
      showDownloadError(t("Installer.downloadFailed"));
      return;
    }
    if (!defaultVersion) {
      showDownloadError(t("Installer.noVersionForHardware", { hardware: hardwareNameOf(model) }));
      return;
    }
    appendLog(`Default version for ${model}: ${defaultVersion.version} (${defaultVersion.source})`);
    await runDownload(defaultVersion.version);
  };

  const handleDownloadConfirm = async () => {
    const version = (versionInput || "").trim();
    if (!version) {
      setNotification({ type: "error", msg: t("Installer.enterVersion") });
      setTimeout(() => setNotification(null), 3000);
      return;
    }
    await runDownload(version);
  };

  const handleReboot = () => {
    // Ask for confirmation via global modal
    dispatch(
      openConfirmation(
        t("Installer.restartTitle"),
        t("Installer.restartMessage"),
        async () => {
          setWorking(true);
          try {
            appendLog("Reboot requested");
            expectDisconnectRef.current = true;
            await rebootDevice((m) => appendLog(m));
            setNotification({ type: "success", msg: t("Reboot.sent") || "Reboot command sent" });
          } catch (e: any) {
            appendLog("Reboot error: " + (e?.message || e));
            setNotification({ type: "error", msg: t("Reboot.failed") || "Reboot failed" });
          } finally {
            setConnected(false);
            setWorking(false);
            setTimeout(() => setNotification(null), 3000);
          }
        },
        true,
        t("close"),
        t("Installer.restart"),
      ),
    );
  };

  return (
    <Container size="sm" my="md">
      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm">
              <Title order={4}>{t("Installer.title")}</Title>
              <Badge color={connected ? "green" : "red"} size="lg">
                {connected ? t("Installer.statusConnected") : t("Installer.statusDisconnected")}
              </Badge>
            </Group>
            <Button
              variant="light"
              onClick={() => navigate("/wifi-setup")}
              rightSection={<IconWifi size={16} />}
              data-testid="installer-wifi-setup-link"
            >
              {t("WifiSetupLink")}
            </Button>
          </Group>

          <Divider />

          <Text>
            {t("Installer.deviceModel")}: {modelInfo}
            {serialInfo ? ` - ${serialInfo}` : ""}
          </Text>

          <Group>
            <Button onClick={handleConnect} disabled={working}>
              {t("Installer.connect")}
            </Button>
            <Button
              onClick={() => {
                setDownloadMode(true);
              }}
              disabled={working}
            >
              {t("Installer.download")}
            </Button>
            <Button onClick={handleReboot} disabled={working}>
              {t("Installer.restart")}
            </Button>
            <Button color="red" onClick={handleUninstall} disabled={working}>
              {t("Installer.uninstall")}
            </Button>
            <Button
              color="blue"
              data-testid="va-download-button"
              onClick={() =>
                dispatch(
                  isVABatchDownloadViewOpen
                    ? closeVABatchDownloadView()
                    : openVABatchDownloadView(),
                )
              }
              disabled={working}
            >
              {t("downloadVAVideos")}
            </Button>
            <Button color="teal" onClick={() => openDeviceSetupPage(navigate)} disabled={working}>
              {t("Installer.doInstallation")}
            </Button>
          </Group>

          <Group>
            <Button
              onClick={handleInstall}
              disabled={working}
              data-testid="installer-install-button"
            >
              {t("install")}
            </Button>
          </Group>

          <Stack gap={4}>
            <Group align="flex-end" gap="sm" wrap="wrap">
              {canOverrideApiConfig && (
                <Select
                  label={t("Installer.apiConfigKey")}
                  placeholder={t("Installer.noApiConfigOverride")}
                  data={apiConfigOptions}
                  value={apiConfigKey}
                  onChange={(value) => {
                    apiConfigKeyTouched.current = true;
                    setApiConfigKey(normalizeConfigKeyToken(value));
                  }}
                  clearable
                  disabled={working}
                  w={260}
                  data-testid="config-set-api-config-key"
                />
              )}
              <Button
                color="blue"
                onClick={handlePushConfigs}
                disabled={working}
                data-testid="installer-config-set-button"
              >
                {t("Installer.configSet")}
              </Button>
            </Group>
            {apiConfigKeyToPush !== null && (
              <Text size="xs" c="dimmed">
                {t("Installer.apiConfigTriggerInstruction")}
              </Text>
            )}
          </Stack>

          {isVABatchDownloadViewOpen && <VABatchDownloadView onLog={logCb} />}

          {downloadMode && (
            <Paper p="md" withBorder radius="md" bg="var(--oculera-surface-alt)">
              <Stack gap="sm">
                <Button
                  fullWidth
                  leftSection={<IconDownload size={16} />}
                  onClick={handleDownloadDefault}
                  disabled={working}
                >
                  {t("Installer.downloadDefault")}
                </Button>

                <Divider label={t("Installer.orEnterSpecificVersion")} labelPosition="center" />

                <Group align="flex-end" gap="sm" wrap="nowrap">
                  <TextInput
                    style={{ flex: 1 }}
                    label={t("Installer.versionLabel")}
                    placeholder={t("Installer.versionPlaceholder")}
                    value={versionInput}
                    onChange={(e) => setVersionInput(e.currentTarget.value)}
                  />
                  <Button
                    variant="light"
                    onClick={handleDownloadConfirm}
                    disabled={working || !versionInput.trim()}
                  >
                    {t("Installer.download")}
                  </Button>
                </Group>

                <Group justify="flex-end">
                  <Button variant="subtle" color="gray" onClick={() => setDownloadMode(false)}>
                    {t("cancel")}
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {downloadProgress && <DownloadProgressBar {...downloadProgress} />}

          <Divider />

          <Text>{t("Installer.logs")}</Text>
          <ScrollArea style={{ height: 220 }} scrollbarSize={10}>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
              {logs.length ? logs.join("\n") : t("Installer.noLogs")}
            </pre>
          </ScrollArea>

          {notification && (
            <Notification color={notification.type === "success" ? "green" : "red"}>
              {notification.msg}
            </Notification>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default InstallerWithWebUsb;
